import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import {
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';


/**
 * @class
 * @extends {EzEnumeration2}
 * Enumeration representing the ezApi registration state of an ezClocker class.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *     import {
 *         // ... other enumeration classes ...
 *         EzRegistrationState
 *     } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes with:
 *     import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access only with:
 *     EzRegistrationState.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzRegistrationState extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Determiens if the provided ezRegistrationState is EzRegistrationState.PENDING
     *
     * @returns {boolean}
     */
    static ezIsStatePendingState(ezRegistrationState) {
        return EzString.hasLength(ezRegistrationState) && EzRegistrationState.PENDING === ezRegistrationState;
    }

    /**
     * @static
     * @public @method
     * Determiens if the provided ezRegistrationState is null/empty string, EzRegistrationState.UNKNOWN,
     * or EzRegistrationState.NOT_REGISTERED
     * @returns {boolean}
     */
    static ezIsNotRegisteredState(ezRegistrationState) {
        if (!EzString.hasLength(ezRegistrationState)) {
            return true;
        }

        return EzRegistrationState.NOT_REGISTERED === ezRegistrationState || EzRegistrationState.UNKNOWN === ezRegistrationState;
    }

    /**
     * @static
     * @public @method
     * Determiens if the provided ezRegistrationState is EzRegistrationState.REGISTERED
     *
     * @returns {boolean}
     */
    static ezIsRegisteredState(ezRegistrationState) {
        return EzString.hasLength(ezRegistrationState) && EzRegistrationState.REGISTERED === ezRegistrationState.toUpperCase();
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Enumeration Class Initialization Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @private @field
     * Stores the singleton instance for the enumeration class
     *
     * @type {EzEnumerationSingleton}
     */
    static #ezEnumerationSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezInit() {
        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            null,
            // Enumeration Data Value
            {
                value: null,
                display: 'Not Registered: Registration of the singleton instance with EzApi has not started and is not complete.'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'NOT_REGISTERED',
            // Enumeration Property Value
            'NOT_REGISTERED',
            // Enumeration Data Value
            {
                value: 'NOT_REGISTERED',
                display: 'Not Registered: Registration of the singleton instance with EzApi has not started or completed.'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'PENDING',
            // Enumeration Property Value
            'PENDING',
            // Enumeration Data Value
            {
                value: 'PENDING',
                display: 'Pending: The class is currently attempting to be has successfully registered its singleton instance with EzApi..'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'REGISTERED',
            // Enumeration Property Value
            'REGISTERED',
            // Enumeration Data Value
            {
                value: 'REGISTERED',
                display: 'Registered: The class has successfully registered its singleton instance with EzApi.'
            });

        this.#ezEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            null,
            // Enumeration Data Value
            {
                value: null,
                display: 'UNKNOWN'
            });
    }

    /**
     * @static
     * Static Initialization
     */
    static {
        if (null == this.#ezEnumerationSingleton) {
            this.#ezEnumerationSingleton = new EzRegistrationState();

            this.#ezInit();
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
